import { FC, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';

import { ItineraryView } from '@mopla/data-models';
import {
	checkTimeMightChange,
	getDurationStr,
	getItineraryTimingV2,
} from '@mopla/utils';

import { DepartureIcon, FlexColumn, IValueProps, Value } from '../../index';

import { DepartureWrapper } from './styled';

interface ITimeDisplayOption {
	variant: IValueProps['variant'];
	icon?: ReactNode;
}

interface ITimeDisplay {
	default: ITimeDisplayOption;
	cancelled: ITimeDisplayOption;
	fixed: ITimeDisplayOption;
	overwritten: ITimeDisplayOption;
}

export interface IItineraryDateTimeInfoProps {
	className?: string;
	route: ItineraryView;
	isCancelled?: boolean;
	showTimeColors?: boolean;
	dateTestId?: string;
	timeTestId?: string;
	overwrittenTimeTestId?: string;
	durationTestId?: string;
	timeDisplayOverride?: Partial<ITimeDisplay>;
	showDurationInfo?: boolean;
}

const defaultTimeDisplayCfg: ITimeDisplay = {
	default: { variant: 'default' },
	cancelled: { variant: 'error' },
	fixed: { variant: 'success' },
	overwritten: { variant: 'attention' },
};

export const ItineraryDateTimeInfo: FC<IItineraryDateTimeInfoProps> = ({
	className,
	route,
	isCancelled,
	showTimeColors,
	dateTestId,
	timeTestId,
	overwrittenTimeTestId,
	durationTestId,
	timeDisplayOverride,
	showDurationInfo = true,
}) => {
	const { t } = useTranslation('common');
	const timeMightChange = checkTimeMightChange(route);
	/** Aggregated by the BE; legacy itineraries carry no count */
	const stopsCount = route.stops ?? 0;

	const itineraryTimings = useMemo(
		() => getItineraryTimingV2(route.legs),
		[route]
	);

	const { timeStr, effectiveTimeStr, startDate, durationStr } = useMemo(() => {
		const {
			effectiveStartTime,
			effectiveEndTime,
			isStartTimeChanged,
			isEndTimeChanged,
			startTime,
			endTime,
			startDate,
			duration,
		} = itineraryTimings;

		const timeStr = `${startTime} - ${endTime}`;
		const effectiveTimeStr =
			(isStartTimeChanged || isEndTimeChanged) &&
			`${effectiveStartTime} - ${effectiveEndTime}`;

		const durationStr = `${t('text.around')} ${getDurationStr(t, duration, [
			'seconds',
		])}`;

		return {
			timeStr,
			effectiveTimeStr,
			startDate,
			durationStr,
			duration,
		};
	}, [itineraryTimings, t]);

	const timeDisplay = useMemo((): ITimeDisplayOption => {
		const timeDisplayConfig = Object.assign(
			{},
			defaultTimeDisplayCfg,
			timeDisplayOverride
		);

		if (!showTimeColors && !timeDisplayOverride) {
			return timeDisplayConfig['default'];
		}

		if (isCancelled) {
			return timeDisplayConfig['cancelled'];
		}

		if (itineraryTimings.isDelayed || itineraryTimings.isShifted) {
			return timeDisplayConfig['overwritten'];
		}

		if (!timeMightChange) {
			return timeDisplayConfig['fixed'];
		}

		return timeDisplayConfig['default'];
	}, [
		isCancelled,
		itineraryTimings,
		showTimeColors,
		timeDisplayOverride,
		timeMightChange,
	]);

	return (
		<FlexColumn className={className}>
			<DepartureWrapper>
				<DepartureIcon />
				<Typography
					data-testid={dateTestId}
					variant="body2"
					color="neutral.100"
				>
					{startDate}
				</Typography>
			</DepartureWrapper>
			{showDurationInfo && (
				<>
					{!isCancelled &&
						(itineraryTimings.isDelayed || itineraryTimings.isShifted) && (
							<Typography
								variant="body2"
								color="neutral.80"
								sx={{ textDecoration: 'line-through' }}
								data-testid={overwrittenTimeTestId}
							>
								{timeStr}
							</Typography>
						)}
					<Value
						value={effectiveTimeStr || timeStr}
						icon={timeDisplay.icon}
						variant={timeDisplay.variant}
						testId={timeTestId}
					/>
					<Typography data-testid={durationTestId} variant="body2">
						{durationStr}
						{stopsCount > 0 &&
							`, ${t('common:text.stops', {
								count: stopsCount,
							})}`}
					</Typography>
				</>
			)}
		</FlexColumn>
	);
};
